import React, { useMemo, useState } from "react";
import { Box } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid";
import { formatCurrency } from "../../../utils/namesFormater";
import { formatPolicyEmploymentStatus, formatProductType } from "../../../utils/enumFormatter";
import StyledDataGrid from "../../../shared/StyledDataGrid";
import { MimshakData } from "../../../interfaces/DTO/mimshakData";
import InsuranceProductDrawer from "../Insurance/InsuranceProductDrawer";
import { Product } from "../../../interfaces/DTO/mislaka_xml_models";
import { adjustedProductTypeColumnFullColor, currencyColumn, handlerCodeColumn, isoDateColumn, policyNumberWithPathTooltipColumn, productStatusChipsColumn, transferOutToColumn } from "../../../shared/UIParts/columnsFormatter";
import { useContactNameColumn } from "../../../hooks/useContactNameColumn";
import { PensionFeeExpenseType } from "../../../interfaces/DTO/mislaka_xml_pension_models";
import { managementFeeColumn } from "../../../shared/UIParts/columnsFormatter";

interface MimshakDataTableProps {
  data: MimshakData[];
  headerColor?: string;
  viewedType: "USER" | "FAMILY";
  selectedPeriodType?: "REGULAR" | "PRE_ADVICE";
  onSelectionChange?: (selectedData: MimshakData[]) => void;
}

const MimshakDataTable: React.FC<MimshakDataTableProps> = ({ data, headerColor, viewedType, selectedPeriodType, onSelectionChange }) => {
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);

  const formattedRows = useMemo(() => {
    return data.map((row, index) => {
      const policy = row.data?.products?.[0]?.policies_or_accounts?.[0];
      const product = row.data?.products?.[0] || null;

      return {
        id: index,
        contact_id: row.contact_id,
        handler_code: row.handler_code,
        reported_period: row.reported_period,
        product_type: formatProductType(row.product_type),
        raw_product_type: row.product_type,
        funds_sum: row.funds_sum,
        policy_number: row.policy_number,
        deposit_amount: row.deposit_amount,
        withdrawal_amount: row.withdrawal_amount,
        transfer_out: row.transfer_out,
        transfer_in: row.transfer_in,
        transfer_out_to: row.transfer_out_to ?? "",
        agent_appointment_date: row.agent_appointment_date,
        new_policy: row.new_policy,
        adjusted_product_type: row.data?.products?.[0]?.details?.adjusted_product_type ?? row.product_type,
        policy_plan_or_account_type: policy ? formatPolicyEmploymentStatus(policy.plan_or_account_type) : "-",
        policy_agent_id: policy?.agent_id_for_producer ?? row.agent_code ?? "-",
        // מהפוליסה (JSON), ואם אין – מהעמודה (בשליפות "light" אין JSON). null ולא "-" – עמודת תאריך
        policy_join_date: policy?.join_date ?? row.join_date ?? null,
        // תאריך הצטרפות למוצר – מהעמודה p_join_date (רק ב-mimshak_data), ואם אין – מהפוליסה
        product_join_date: row.p_join_date ?? policy?.product_join_date ?? null,
        product,
        data_access_type: row.data_access_type,
        data: row.data,
      };
    });
  }, [data]);

  const contactNameColumn = useContactNameColumn();

  const columns: GridColDef[] = useMemo(() => {
    const baseCols: GridColDef[] = [
      adjustedProductTypeColumnFullColor(),
      policyNumberWithPathTooltipColumn(),
      handlerCodeColumn(),
      productStatusChipsColumn(),
      {
        field: "policy_plan_or_account_type",
        headerName: "סוג תוכנית",
        width: 100,
      },
      currencyColumn("funds_sum", "יתרה כוללת", 100, true),
      managementFeeColumn("saving_fee", "ד.נ צבירה", PensionFeeExpenseType.FROM_SAVINGS, 100),
      managementFeeColumn("deposit_fee", "ד.נ הפקדה", PensionFeeExpenseType.FROM_DEPOSITS, 100),
      currencyColumn("deposit_amount", "סך הפקדות", 100, false),
      currencyColumn("withdrawal_amount", "סך משיכות", 100, false),
      currencyColumn("transfer_out", "העברות החוצה", 100, true),
      currencyColumn("transfer_in", "העברות פנימה", 100, false),
      transferOutToColumn(),
      isoDateColumn("agent_appointment_date", "תאריך מינוי סוכן", 100),
      { field: "policy_agent_id", headerName: "מזהה סוכן", width: 100 },
      isoDateColumn("policy_join_date", "תאריך פתיחה", 100),
      isoDateColumn("product_join_date", "הצטרפות למוצר", 100),
      { field: "reported_period", headerName: "תקופה מדווחת", width: 140 },
    ];

    if (viewedType === "FAMILY") {
      baseCols.unshift(contactNameColumn);
    }

    const filteredCols = selectedPeriodType === "PRE_ADVICE"
      ? baseCols.filter(
          (col) => col.field !== "transfer_out" && col.field !== "transfer_in" && col.field !== "transfer_out_to"
        )
      : baseCols;

    return filteredCols.map((col) => ({
      ...col,
      headerClassName: "mimshak-table-header",
      headerAlign: "center",
    }));
  }, [selectedPeriodType, viewedType, contactNameColumn]);

  const selectedData = useMemo(() => {
    if (selectedRows.length === 0) return data;
    return data.filter((_, index) => selectedRows.includes(index));
  }, [data, selectedRows]);

  const totals = useMemo(() => ({
    funds_sum: selectedData.reduce((sum, r) => sum + (r.funds_sum || 0), 0),
    deposit_amount: selectedData.reduce((sum, r) => sum + (r.deposit_amount || 0), 0),
    withdrawal_amount: selectedData.reduce((sum, r) => sum + (r.withdrawal_amount || 0), 0),
    transfer_out: selectedData.reduce((sum, r) => sum + (r.transfer_out || 0), 0),
    transfer_in: selectedData.reduce((sum, r) => sum + (r.transfer_in || 0), 0),
  }), [selectedData]);

  const handleRowClick = (params: any) => {
    const originalRow = data[params.id];
    setSelectedProduct(originalRow.data?.products[0] || null);
  };

  return (
    <Box dir="rtl" sx={{ maxWidth: "100%", margin: "auto", padding: 2 }}>
      <StyledDataGrid
        rows={formattedRows}
        columns={columns}
        onCellClick={handleRowClick}
        checkboxSelection
        maxHeight="600px"
        disableRowSelectionOnClick
        rowSelectionModel={selectedRows}
        onRowSelectionModelChange={(newSelection) => {
          const indices = newSelection as number[];
          setSelectedRows(indices);
          onSelectionChange?.(data.filter((_, i) => indices.includes(i)));
        }}
        hideFooter={formattedRows.length <= 100 && selectedRows.length === 0}
      />

      <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 4, px: 2 }}>
        {data[0]?.reported_period && (
          <Box>לתקופת דיווח: <strong>{data[0].reported_period}</strong></Box>
        )}
        {totals.funds_sum ? (
          <Box>סה"כ יתרה כוללת: <strong><span dir="ltr">{formatCurrency(totals.funds_sum)}</span></strong></Box>
        ) : null}
        {totals.deposit_amount ? (
          <Box>סה"כ הפקדות: <strong><span dir="ltr">{formatCurrency(totals.deposit_amount)}</span></strong></Box>
        ) : null}
        {totals.withdrawal_amount ? (
          <Box>סה"כ משיכות: <strong><span dir="ltr">{formatCurrency(totals.withdrawal_amount)}</span></strong></Box>
        ) : null}
        {totals.transfer_out ? (
          <Box>סה"כ העברות החוצה: <strong><span dir="ltr">{formatCurrency(totals.transfer_out)}</span></strong></Box>
        ) : null}
        {totals.transfer_in ? (
          <Box>סה"כ העברות פנימה: <strong><span dir="ltr">{formatCurrency(totals.transfer_in)}</span></strong></Box>
        ) : null}
      </Box>

      {selectedProduct && (
        <InsuranceProductDrawer product={selectedProduct} onClose={() => setSelectedProduct(null)} />
      )}
    </Box>
  );
};

export default MimshakDataTable;
